import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilePdfIcon } from '@phosphor-icons/react';
import type { FrakoblingBlokkering } from '@origo/planregister';
import { Button } from '@origo/origo-ui';
import { EmptyState } from '@geodata/georg';
import PlanDetailHeader from '../../components/PlanDetailHeader';
import DocumentList from './components/DocumentList';
import NewDocumentDialog from './components/NewDocumentDialog';
import DeleteDocumentDialog from './components/DeleteDocumentDialog';
import type { DocumentFieldErrors } from './components/DocumentForm';
import type { DocumentFormValues, DocumentItem } from '../../types';
import styles from './DocumentsPage.module.css';

export interface NewDocumentState {
    open: boolean;
    values: DocumentFormValues;
    file: File | null;
    fieldErrors: DocumentFieldErrors;
    fileError?: string;
    isSaving: boolean;
}

interface DocumentsPageProps {
    items: DocumentItem[];
    onOpen: (id: string) => void;

    newDocument: NewDocumentState;
    onNewOpen: () => void;
    onNewClose: () => void;
    onNewChange: (field: keyof DocumentFormValues, value: string) => void;
    onNewFileChange: (file: File | null) => void;
    onNewSave: () => void;

    onDeleteRequest: (id: string) => void;
    pendingDelete: DocumentItem | null;
    /** Satt når `pendingDelete` ikke kan slettes (bestemmelser på vedtatt plan, #14900). */
    deleteBlokkering: FrakoblingBlokkering | null;
    /** Sperren for `pendingDelete` kan ikke avgjøres ennå (planen lastes). */
    deleteAvventerPlan?: boolean;
    onDeleteConfirm: () => void;
    onDeleteCancel: () => void;
    isDeleting?: boolean;
}

const DocumentsPage: React.FC<DocumentsPageProps> = ({
    items,
    onOpen,
    newDocument,
    onNewOpen,
    onNewClose,
    onNewChange,
    onNewFileChange,
    onNewSave,
    onDeleteRequest,
    pendingDelete,
    deleteBlokkering,
    deleteAvventerPlan,
    onDeleteConfirm,
    onDeleteCancel,
    isDeleting,
}) => {
    const { t } = useTranslation();

    const isEmpty = items.length === 0;

    return (
        <article className={styles.page}>
            <PlanDetailHeader
                title={t('documents.title')}
                subtitle={t('documents.subtitle')}
                actions={
                    !isEmpty ? (
                        <Button variant="secondary" size="md" onClick={onNewOpen}>
                            {t('documents.newDocument')}
                        </Button>
                    ) : undefined
                }
            />
            <div className={styles.body}>
                {isEmpty ? (
                    <EmptyState
                        icon={<FilePdfIcon size={32} weight="duotone" />}
                        title={t('documents.empty.title')}
                        description={t('documents.empty.description')}
                        action={
                            <Button variant="primary" size="lg" onClick={onNewOpen}>
                                {t('documents.empty.action')}
                            </Button>
                        }
                    />
                ) : (
                    <DocumentList
                        items={items}
                        onOpen={onOpen}
                        onAction={(_action, id) => onDeleteRequest(id)}
                    />
                )}
            </div>
            <NewDocumentDialog
                open={newDocument.open}
                values={newDocument.values}
                file={newDocument.file}
                fieldErrors={newDocument.fieldErrors}
                fileError={newDocument.fileError}
                onChange={onNewChange}
                onFileChange={onNewFileChange}
                onClose={onNewClose}
                onSave={onNewSave}
                isSaving={newDocument.isSaving}
            />
            <DeleteDocumentDialog
                pendingDelete={pendingDelete}
                blokkering={deleteBlokkering}
                avventerSjekk={deleteAvventerPlan}
                onConfirm={onDeleteConfirm}
                onCancel={onDeleteCancel}
                isDeleting={isDeleting}
            />
        </article>
    );
};

export default DocumentsPage;
